import React, { useEffect, useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router';
import { Button, Card, Dropdown, IconButton, Input, Menu, MenuButton, MenuItem, Stack, Typography } from '@mui/joy';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import ShareIcon from '@mui/icons-material/Share';
import { Round, RoundStatus } from '../models/round';
import { Score } from '../models/player';
import HoleList from './HoleList';
import ShareRoundModal from './ShareRoundModal';
import { serviceFacade } from '../services/service-facade';
import { HttpError } from '../services/round-service';
import { computeParOffset, formatOffset } from '../utils/par';

const RoundComponent = () => {
    const { round } = useLoaderData();
    const navigate = useNavigate();
    const [currentRound, setCurrentRound] = useState<Round>(round);

    const isCourseRound = !!currentRound.courseName;
    const lastHole =
        isCourseRound && currentRound.holes?.length ? Math.max(...currentRound.holes.map((h) => h.number)) : null;

    // Resume on the hole after the last scored one, but never past the course's
    // final hole (otherwise a fully scored course round would overshoot on reload).
    const resumeHole = round.player.scores.length + 1;
    const initialHole = lastHole !== null ? Math.min(resumeHole, lastHole) : resumeHole;
    const initialScore = round.player.scores.find((s: Score) => s.hole === initialHole)?.score ?? 0;

    const [currentHole, setCurrentHole] = useState<number>(initialHole);
    const [currentScore, setCurrentScore] = useState<number>(initialScore);
    const [localPar, setLocalPar] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);

    const holePar = currentRound.holes?.find((h) => h.number === currentHole)?.par ?? null;
    const isOwner = currentRound.player?.id === currentRound.ownerPlayerId;

    useEffect(() => {
        setLocalPar('');
    }, [currentHole, holePar]);

    const handlePlusOne = () => {
        setAuthError(null);
        setCurrentScore((prev) => prev + 1);
    };

    const handleMinusOne = () => {
        setAuthError(null);
        setCurrentScore((prev) => Math.max(prev - 1, 0));
    };

    const handleHoleNavigation = async (offset: number) => {
        try {
            setIsLoading(true);
            setAuthError(null);
            let updatedRound = currentRound;
            if (currentScore !== 0) {
                updatedRound = await serviceFacade.updateScore(
                    currentRound.id,
                    currentRound.player!.id,
                    currentHole,
                    currentScore
                );
                setCurrentRound(updatedRound);
            }
            const nextHole = currentHole + offset;
            setCurrentHole(nextHole);
            const nextHoleScore = updatedRound.player!.scores.find((s: Score) => s.hole === nextHole);
            setCurrentScore(nextHoleScore ? nextHoleScore.score : 0);
        } catch (err: unknown) {
            if (err instanceof HttpError && err.status === 401) {
                setAuthError('You are not authenticated. Try refreshing the page.');
            } else if (err instanceof HttpError && err.status === 403) {
                setAuthError("You don't have permission to record this score.");
            } else {
                throw err;
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePreviousHole = async () => {
        if (currentHole <= 1) return;
        await handleHoleNavigation(-1);
    };

    const handleNextHole = async () => {
        await handleHoleNavigation(1);
    };

    const handleSaveScore = async () => {
        try {
            setIsLoading(true);
            setAuthError(null);
            const updatedRound = await serviceFacade.updateScore(
                currentRound.id,
                currentRound.player!.id,
                currentHole,
                currentScore
            );
            setCurrentRound(updatedRound);
        } catch (err: unknown) {
            if (err instanceof HttpError && err.status === 401) {
                setAuthError('You are not authenticated. Try refreshing the page.');
            } else if (err instanceof HttpError && err.status === 403) {
                setAuthError("You don't have permission to record this score.");
            } else {
                throw err;
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSetPar = async (value: string) => {
        const par = parseInt(value, 10);
        if (isNaN(par) || par < 1) return;
        try {
            setIsLoading(true);
            const updated = await serviceFacade.setHolePar(currentRound.id, currentRound.player!.id, currentHole, par);
            setCurrentRound(updated);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCompleteRound = async () => {
        try {
            setIsLoading(true);
            if (currentScore !== 0) {
                await serviceFacade.updateScore(currentRound.id, currentRound.player!.id, currentHole, currentScore);
            }
            const updatedRound = await serviceFacade.completeRound(currentRound.id, currentRound.player!.id);
            setCurrentRound(updatedRound);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditRound = async () => {
        try {
            setIsLoading(true);
            const updatedRound = await serviceFacade.editRound(currentRound.id, currentRound.player!.id);
            setCurrentRound(updatedRound);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveAsCourse = () => {
        navigate('/courses/new', {
            state: { name: currentRound.name, holes: currentRound.holes ?? [] },
        });
    };

    const handleRefresh = async () => {
        try {
            setIsLoading(true);
            const refreshed = await serviceFacade.getRound(currentRound.id, currentRound.player!.id);
            setCurrentRound(refreshed);
        } finally {
            setIsLoading(false);
        }
    };

    const isReadOnly = () => {
        return currentRound?.player?.status === RoundStatus.COMPLETED;
    };

    const playerScores = currentRound.player?.scores ?? [];
    const totalScore = playerScores.reduce((acc, s) => acc + s.score, 0);
    const parOffset = computeParOffset(playerScores, currentRound.holes ?? []);
    const holesPlayed = playerScores.length;

    return (
        <Stack spacing={3}>
            <Card variant="outlined" className="round-card">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack spacing={0.5}>
                        <Typography level="h3">{currentRound.name}</Typography>
                        {currentRound.courseName && <Typography level="body-sm">{currentRound.courseName}</Typography>}
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Dropdown>
                            <MenuButton
                                aria-label="Round actions"
                                slots={{ root: IconButton }}
                                slotProps={{ root: { variant: 'plain', color: 'neutral' } }}
                            >
                                <MoreVertIcon />
                            </MenuButton>
                            <Menu placement="bottom-end">
                                <MenuItem onClick={() => setIsShareOpen(true)}>
                                    <ShareIcon fontSize="small" />
                                    &nbsp;Share Round
                                </MenuItem>
                                <MenuItem onClick={handleRefresh} disabled={isLoading}>
                                    <RefreshIcon fontSize="small" />
                                    &nbsp;Refresh
                                </MenuItem>
                                <MenuItem onClick={handleSaveAsCourse}>
                                    <SaveIcon fontSize="small" />
                                    &nbsp;Save as Course
                                </MenuItem>
                                <MenuItem
                                    onClick={isReadOnly() ? handleEditRound : handleCompleteRound}
                                    disabled={isLoading}
                                >
                                    {isReadOnly() ? <EditIcon fontSize="small" /> : <CheckIcon fontSize="small" />}
                                    &nbsp;{isReadOnly() ? 'Edit Round' : 'Finish Round'}
                                </MenuItem>
                            </Menu>
                        </Dropdown>
                    </Stack>
                </Stack>
            </Card>

            {!isReadOnly() && (
                <Card variant="soft" color="primary" className="score-card">
                    <Stack spacing={2} alignItems="center">
                        <Stack direction="row" spacing={2} width="100%" justifyContent="space-between">
                            <Button
                                size="lg"
                                variant="outlined"
                                color="primary"
                                onClick={handleMinusOne}
                                fullWidth={true}
                                disabled={currentScore === 0}
                            >
                                -1
                            </Button>
                            <Stack spacing={1} whiteSpace={'nowrap'} alignItems="center">
                                <Typography level="h4">Hole {currentHole}</Typography>
                                {holePar !== null ? (
                                    <Typography level="body-sm">Par {holePar}</Typography>
                                ) : isOwner ? (
                                    <Input
                                        size="sm"
                                        type="number"
                                        placeholder="Par?"
                                        value={localPar}
                                        onChange={(e) => setLocalPar(e.target.value)}
                                        onBlur={(e) => handleSetPar(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleSetPar(localPar);
                                        }}
                                        slotProps={{ input: { min: 1, max: 10, style: { width: 64 } } }}
                                        aria-label="Par"
                                    />
                                ) : (
                                    <Typography level="body-sm">Par —</Typography>
                                )}
                                <Typography level="h1" className="score-display">
                                    {currentScore}
                                </Typography>
                            </Stack>
                            <Button size="lg" variant="solid" color="primary" fullWidth={true} onClick={handlePlusOne}>
                                +1
                            </Button>
                        </Stack>

                        {holesPlayed > 0 && (
                            <Typography level="body-sm">
                                {parOffset !== null
                                    ? `${totalScore} (${formatOffset(parOffset)}) through ${holesPlayed} hole${holesPlayed !== 1 ? 's' : ''}`
                                    : `${totalScore} through ${holesPlayed} hole${holesPlayed !== 1 ? 's' : ''}`}
                            </Typography>
                        )}

                        <Stack direction="row" spacing={2} width="100%" justifyContent="space-between">
                            <Button
                                size="lg"
                                variant="outlined"
                                color="neutral"
                                fullWidth={true}
                                onClick={handlePreviousHole}
                                disabled={currentHole <= 1 || isLoading}
                                loading={isLoading}
                            >
                                Previous Hole
                            </Button>

                            {lastHole !== null && currentHole >= lastHole ? (
                                <Button
                                    size="lg"
                                    variant="outlined"
                                    color="neutral"
                                    fullWidth={true}
                                    onClick={handleSaveScore}
                                    disabled={currentScore === 0 || isLoading}
                                    loading={isLoading}
                                >
                                    Save Score
                                </Button>
                            ) : (
                                <Button
                                    size="lg"
                                    variant="outlined"
                                    color="neutral"
                                    fullWidth={true}
                                    onClick={handleNextHole}
                                    disabled={currentScore === 0 || isLoading}
                                    loading={isLoading}
                                >
                                    Next Hole
                                </Button>
                            )}
                        </Stack>

                        {authError && (
                            <Typography level="body-sm" color="danger">
                                {authError}
                            </Typography>
                        )}
                    </Stack>
                </Card>
            )}

            <HoleList currentRound={currentRound} currentPlayerId={currentRound.player!.id} />

            <ShareRoundModal open={isShareOpen} roundId={currentRound.id} onClose={() => setIsShareOpen(false)} />
        </Stack>
    );
};

export default RoundComponent;
